/**
 * Field Renderer
 * Automatically renders the appropriate field component based on schema type
 */

import React, { useMemo } from 'react';
import { FieldType, FieldSchema, ValidationResult } from '@/config/fieldConfig';
import { useI18n } from '@/i18n';
import TextField from './TextField';
import TextareaField from './TextareaField';
import NumberField from './NumberField';
import SelectField from './SelectField';
import CheckboxField from './CheckboxField';
import MarkdownField from './MarkdownField';
import JsonField from './JsonField';
import AgentMultiSelectField from './AgentMultiSelectField';
import ModelSelectorField from './ModelSelectorField';
import OutputFormatField from './OutputFormatField';

export interface FieldRendererProps {
  name: string;
  value: any;
  onChange: (value: any) => void;
  schema: FieldSchema;
  error?: ValidationResult;
  disabled?: boolean;
  appContext?: any;
  onBrowseTools?: () => void;
  /** Full form data for compound fields (e.g., model selector with parameters) */
  formData?: Record<string, unknown>;
  /** Callback to update form data for compound fields */
  onFormDataChange?: (path: string, value: unknown) => void;
}

export const FieldRenderer: React.FC<FieldRendererProps> = ({
  name,
  value,
  onChange,
  schema,
  error,
  disabled = false,
  appContext,
  onBrowseTools,
  formData,
  onFormDataChange,
}) => {
  const { getFieldTranslation, language } = useI18n();

  // Merge i18n translations into schema (unless overrideI18n is set)
  const translatedSchema = useMemo<FieldSchema>(() => {
    // If schema explicitly overrides i18n, use schema values directly
    if (schema.overrideI18n) {
      return schema;
    }

    const translation = getFieldTranslation(name);
    if (!translation) {
      return schema;
    }

    // Create a new schema with i18n values merged
    const result: FieldSchema = {
      ...schema,
      label: translation.label || schema.label,
      help: translation.help || schema.help,
      placeholder: translation.placeholder || schema.placeholder,
    };

    // If translation has options, merge them with schema options
    if (translation.options && schema.options) {
      result.options = schema.options.map(opt => ({
        ...opt,
        label: translation.options?.[opt.value] || opt.label,
      }));
    }

    return result;
  }, [schema, name, getFieldTranslation, language]);

  // Check if field should be shown based on showIf condition
  if (schema.showIf && appContext) {
    const shouldShow = schema.showIf(appContext);
    if (!shouldShow) {
      return null;
    }
  }

  // Check if field should be disabled based on disabledIf condition
  // Use formData for disabledIf since it contains the actual form values (hooks.*.enabled, etc.)
  // appContext may contain other data like model lists
  const configData = formData || appContext;
  const isDisabled = disabled || (schema.disabledIf && configData ? schema.disabledIf(configData) : false);

  // Render appropriate field component based on type
  switch (translatedSchema.type) {
    case FieldType.TEXT:
      return (
        <TextField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={isDisabled}
        />
      );

    case FieldType.TEXTAREA:
      return (
        <TextareaField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={isDisabled}
        />
      );

    case FieldType.NUMBER:
      return (
        <NumberField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={isDisabled}
        />
      );

    case FieldType.SELECT:
      return (
        <SelectField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={isDisabled}
          appContext={appContext}
        />
      );

    case FieldType.CHECKBOX:
      return (
        <CheckboxField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={isDisabled}
        />
      );

    case FieldType.MARKDOWN:
      return (
        <MarkdownField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={isDisabled}
        />
      );

    case FieldType.JSON:
      return (
        <JsonField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={isDisabled}
          onBrowseTools={onBrowseTools}
        />
      );

    case FieldType.AGENT_MULTI_SELECT:
      return (
        <AgentMultiSelectField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error?.message}
          disabled={isDisabled}
          currentAgentType={appContext?.agent_type}
        />
      );

    case FieldType.MODEL_SELECTOR:
      return (
        <ModelSelectorField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={isDisabled}
          appContext={appContext}
          formData={formData}
          onFormDataChange={onFormDataChange}
        />
      );

    case FieldType.OUTPUT_FORMAT:
      return (
        <OutputFormatField
          name={name}
          value={value}
          onChange={onChange}
          schema={translatedSchema}
          error={error}
          disabled={isDisabled}
          appContext={appContext}
          onFormDataChange={onFormDataChange}
        />
      );

    default:
      console.warn(`Unknown field type: ${translatedSchema.type} for field ${name}`);
      return (
        <div className="text-red-600 text-sm">
          Error: Unknown field type "{translatedSchema.type}"
        </div>
      );
  }
};

export default FieldRenderer;
